'use client'

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { Database } from '@/types/database'
import { motion } from 'framer-motion'
import { ArrowLeft, User, Sparkles, Crown, Building2 } from 'lucide-react'
import { useFacilityBranding, useOrgVocab } from '@/contexts/SubdomainBrandingContext'
import { shouldShowAge } from '@/utils/textClean'
import SEO from '@/components/layout/SEO'

// ─── Design tokens (matches Dashboard) ──────────────────────────────
const T = {
  sage: '#4a7c5e', sageDark: '#2d5a3d', sageDeep: '#1e3d2b',
  sageLight: '#e8f2ec', sageMid: '#b8d9c4',
  cream: '#faf7f2', creamDark: '#f0ebe0',
  gold: '#c9903a', navy: '#1a2740', navyMid: '#243452',
  white: '#ffffff', slate: '#4a5568', muted: '#8a94a6',
  fontDisplay: "'Playfair Display', Georgia, serif",
  fontBody: "'Source Serif 4', Georgia, serif",
}

type UserProfile = Database['public']['Tables']['users']['Row']
type AgeBracket = '' | 'under_30' | '30_to_49' | '50_plus'
type Gender = 'male' | 'female' | 'unspecified'
type Persona = '' | 'Mentor' | 'Coach' | 'Sidekick'
type ExtendedUserProfile = UserProfile & {
  age_bracket?: AgeBracket | null; gender?: Gender | null
  preferred_persona?: Persona | null; country_code?: string | null
}

export default function ProfilePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, userProfile, isLoading: authLoading, updateProfile } = useAuth()
  const branding = useFacilityBranding()
  const vocab = useOrgVocab()

  // ── White-label context ──────────────────────────────────────────────────
  // Org users (seat-based tenants like Big Dog Building) never see retail
  // concepts — subscription tier, age brackets, AI persona. Those belong to
  // the consumer academy only.
  const facilityRole: string | null | undefined = (userProfile as any)?.facility_role
  const isOrgUser = Boolean((userProfile as any)?.facility_id || branding?.facility_id)
  const isStaff = facilityRole === 'admin' || facilityRole === 'teacher'
  const orgName = branding?.display_name || ''
  const primary = branding?.primary_color || T.sage
  const teacherLabel = (vocab as any)?.teacherSingular || (vocab as any)?.educatorSingular || 'Course Manager'
  const showLearnerFields = !isStaff && (!isOrgUser || shouldShowAge(branding?.org_type))
  // Staff return to their console, not the student transcript
  const backTo = isStaff ? '/education-admin' : '/learn'

  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const [formData, setFormData] = useState({
    name: '', age: null as number | null, age_bracket: '' as AgeBracket,
    gender: '' as '' | Gender, preferred_persona: '' as Persona,
    country_code: '' as string, preferences: {} as Record<string, unknown>
  })

  useEffect(() => { if (!authLoading && !user) navigate('/auth/login') }, [user?.id, authLoading, navigate])

  useEffect(() => {
    if (userProfile) {
      const normalized = { ...userProfile, subscription_status: (userProfile.subscription_status || 'free') as 'free' | 'premium' | 'cancelled' }
      setProfile(normalized as any)
      setFormData({
        name: userProfile.name || '', age: userProfile.age,
        age_bracket: (userProfile as ExtendedUserProfile).age_bracket || '',
        gender: (userProfile as ExtendedUserProfile).gender || 'unspecified',
        preferred_persona: (userProfile as ExtendedUserProfile).preferred_persona || '',
        country_code: (userProfile as ExtendedUserProfile).country_code || '',
        preferences: (userProfile.preferences || {}) as Record<string, unknown>
      })
      setIsLoading(false)
    }
  }, [userProfile])

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    if (!user) return
    setIsSaving(true); setSaveMessage('')
    try {
      const fullUpdate = { ...formData, preferences: formData.preferences as Database['public']['Tables']['users']['Row']['preferences'] }
      const { error } = await updateProfile(fullUpdate as any)
      if (error) { setSaveMessage(t('profile.error')); console.error('Error updating profile:', error) }
      else { setSaveMessage(t('profile.saved')); setTimeout(() => setSaveMessage(''), 3000) }
    } catch (error) { setSaveMessage(t('profile.error')); console.error('Error updating profile:', error) }
    finally { setIsSaving(false) }
  }

  const isPremium = profile?.subscription_status === 'premium'
  const userName = userProfile?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'Friend'

  if (authLoading || isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: T.navy, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) return null

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '14px 16px', borderRadius: '14px',
    background: T.cream, border: `1px solid ${T.creamDark}`,
    fontSize: '16px', fontFamily: T.fontBody, color: T.navy,
    outline: 'none', transition: 'border-color 0.15s',
  }

  const selectStyle: React.CSSProperties = {
    ...inputStyle, cursor: 'pointer', appearance: 'auto' as any,
  }

  const labelStyle: React.CSSProperties = {
    fontSize: '11px', fontWeight: 800, color: T.muted,
    textTransform: 'uppercase', letterSpacing: '0.1em',
    display: 'block', marginBottom: '6px',
  }

  const cardStyle: React.CSSProperties = {
    background: T.white, borderRadius: '20px', padding: '32px',
    border: `1px solid ${T.creamDark}`, boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  }

  return (
    <div style={{ minHeight: '100vh', background: T.cream, fontFamily: T.fontBody }}>
      <SEO title="Profile" noindex />

      {/* ══ NAVY HERO BAND ══ */}
      <div style={{ background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 60%, ${isOrgUser ? primary : T.sageDeep} 100%)`, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '300px', height: '300px', borderRadius: '50%', border: '1px solid rgba(184,217,196,0.12)', pointerEvents: 'none' }} />

        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '28px 20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button onClick={() => navigate(backTo)} style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: T.sageMid }}>
              <ArrowLeft size={18} />
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              {branding?.logo_url ? (
                <img src={branding.logo_url} alt={orgName || 'Organisation'} style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover', background: T.white, boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }} />
              ) : (
                <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: isOrgUser ? primary : `linear-gradient(135deg, ${T.gold} 0%, rgba(201,144,58,0.7) 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.white, fontWeight: 900, fontSize: '22px', fontFamily: T.fontDisplay, boxShadow: isOrgUser ? '0 4px 16px rgba(0,0,0,0.25)' : '0 4px 16px rgba(201,144,58,0.35)' }}>
                  {userName.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h1 style={{ fontFamily: T.fontDisplay, fontSize: '28px', fontWeight: 700, color: T.white, margin: 0, lineHeight: 1.2 }}>
                  {userName}'s Profile
                </h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  {isOrgUser ? (
                    <span style={{ fontSize: '12px', fontWeight: 700, color: T.sageMid, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Building2 size={12} /> {orgName || 'Organisation'}{isStaff ? ` · ${facilityRole === 'admin' ? 'Administrator' : teacherLabel}` : ''}
                    </span>
                  ) : isPremium ? (
                    <span style={{ fontSize: '12px', fontWeight: 700, color: T.gold, display: 'flex', alignItems: 'center', gap: '4px' }}><Crown size={12} /> Premium Member</span>
                  ) : (
                    <span style={{ fontSize: '12px', fontWeight: 700, color: T.sageMid, display: 'flex', alignItems: 'center', gap: '4px' }}><Sparkles size={12} /> Free Member</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <svg viewBox="0 0 1440 48" style={{ display: 'block', width: '100%', height: '48px' }} preserveAspectRatio="none">
          <path d="M0,48 C360,0 1080,48 1440,20 L1440,48 Z" fill={T.cream} />
        </svg>
      </div>

      {/* ══ CONTENT ══ */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 20px 80px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>

          {/* Personal Information */}
          <div style={cardStyle}>
            <h2 style={{ fontFamily: T.fontDisplay, fontSize: '22px', fontWeight: 700, color: T.navy, margin: '0 0 24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <User size={20} color={T.sage} /> {t('profile.personalInfo.title')}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div>
                <label style={labelStyle}>{t('profile.personalInfo.name')}</label>
                <input type="text" value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder={t('profile.personalInfo.namePlaceholder')} style={inputStyle} />
              </div>

              <div>
                <label style={labelStyle}>{t('profile.personalInfo.email')}</label>
                <input type="email" value={user.email || ''} disabled style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }} />
                <p style={{ fontSize: '12px', color: T.muted, margin: '4px 0 0' }}>{t('profile.email_cannot_be_changed')}</p>
              </div>

              {showLearnerFields && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '14px' }}>
                <div>
                  <label style={labelStyle}>{t('profile.personalInfo.age')}</label>
                  <input type="number" value={formData.age || ''} onChange={(e) => handleInputChange('age', parseInt(e.target.value) || 0)}
                    placeholder={t('profile.personalInfo.agePlaceholder')} style={inputStyle} min={18} max={120} />
                </div>
                <div>
                  <label style={labelStyle}>{t('profile.age_bracket')}</label>
                  <select value={formData.age_bracket} onChange={(e) => handleInputChange('age_bracket', e.target.value)} style={selectStyle}>
                    <option value="">{t('profile.select')}</option>
                    <option value="under_30">{t('profile.under_30')}</option>
                    <option value="30_to_49">30 - 49</option>
                    <option value="50_plus">50+</option>
                  </select>
                </div>
              </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: '14px' }}>
                <div>
                  <label style={labelStyle}>Gender</label>
                  <select value={formData.gender} onChange={(e) => handleInputChange('gender', e.target.value)} style={selectStyle}>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="unspecified">{t('profile.prefer_not_to_say')}</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Country</label>
                  <input type="text" value={formData.country_code || ''} onChange={(e) => handleInputChange('country_code', e.target.value)}
                    placeholder={t('profile.us_gb')} style={inputStyle} maxLength={2} />
                </div>
              </div>

              {showLearnerFields && (
              <div>
                <label style={labelStyle}>{t('profile.preferred_ai_persona')}</label>
                <select value={formData.preferred_persona} onChange={(e) => handleInputChange('preferred_persona', e.target.value)} style={selectStyle}>
                  <option value="">{t('profile.auto_based_on_age')}</option>
                  <option value="Mentor">{t('profile.the_mentor')}</option>
                  <option value="Coach">{t('profile.the_coach')}</option>
                  <option value="Sidekick">{t('profile.the_sidekick')}</option>
                </select>
              </div>
              )}

              <div style={{ marginTop: '24px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button onClick={handleSave} disabled={isSaving}
                  style={{ padding: '14px 32px', borderRadius: '14px', background: primary, color: T.white, border: 'none', fontSize: '16px', fontWeight: 700, cursor: 'pointer', fontFamily: T.fontBody, boxShadow: `0 4px 16px rgba(0,0,0,0.18)`, opacity: isSaving ? 0.6 : 1 }}>
                  {isSaving ? t('profile.saving') : t('profile.saveButton')}
                </button>
              </div>

              {saveMessage && (
                <div style={{ marginTop: '20px', padding: '12px', borderRadius: '12px', fontWeight: 700, fontSize: '14px', textAlign: 'center', background: saveMessage === t('profile.saved') ? T.sageLight : '#fef2f2', color: saveMessage === t('profile.saved') ? T.sageDark : '#dc2626' }}>
                  {saveMessage}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={cardStyle}>
              {isOrgUser ? (
                <>
                  <h3 style={{ fontFamily: T.fontDisplay, fontSize: '18px', fontWeight: 700, color: T.navy, margin: '0 0 16px' }}>{orgName || 'Organisation'}</h3>
                  <div>
                    <p style={{ fontSize: '13px', color: T.muted, margin: '0 0 4px' }}>{isStaff ? 'Access' : 'Membership'}</p>
                    <p style={{ fontSize: '18px', fontWeight: 700, color: primary, margin: 0 }}>
                      {facilityRole === 'admin' ? 'Administrator' : facilityRole === 'teacher' ? teacherLabel : vocab.learnerSingular}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <h3 style={{ fontFamily: T.fontDisplay, fontSize: '18px', fontWeight: 700, color: T.navy, margin: '0 0 16px' }}>{t('profile.subscription.title')}</h3>
                  <div>
                    <p style={{ fontSize: '13px', color: T.muted, margin: '0 0 4px' }}>{t('profile.subscription.status')}</p>
                    <p style={{ fontSize: '18px', fontWeight: 700, color: isPremium ? T.gold : T.sage, margin: 0 }}>
                      {isPremium ? t('profile.subscription.premium') : t('profile.subscription.free')}
                    </p>
                  </div>
                </>
              )}
              <div style={{ marginTop: '16px' }}>
                <p style={{ fontSize: '13px', color: T.muted, margin: '0 0 4px' }}>{t('profile.member_since')}</p>
                <p style={{ fontSize: '14px', fontWeight: 600, color: T.navy, margin: 0 }}>
                  {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}
